import { Deferred } from "@use/deferred";

/**
 * This will cap concurrency of the given callback at the desired threshold,
 * preventing intensive actions from occurring too many times at once.
 */
export function limitConcurrency<A extends unknown[], T>(
    callback: (...args: A) => Promise<T> | PromiseLike<T>,
    threshold: number = 1,
): (...args: A) => Promise<T> {
    // Create a callback queue, which we're going to use to manage currently
    // active calls...
    const queue = new Set<() => Promise<void>>();

    // Return a method that'll push a callback to the queue
    return async (...args: A) => {
        // Create a unique promise for this execution, which we're going to
        // resolve once it's been carried out...
        const promise = new Deferred<T>();

        // Create a wrapped callback, which we can push to our execution queue
        const wrappedCallback = async () => {
            try {
                // Run the originally given callback, and resolve the external
                // promise once it's completed
                promise.resolve(await callback(...args));
            } catch (err) {
                // If something goes wrong during execution, then reject the
                // external promise...
                promise.reject(<unknown> err);
            } finally {
                // Once execution has finished, remove the callback from the
                // execution queue to free up space for next requests
                queue.delete(wrappedCallback);

                // If there are any requests queued up, then now is the time to
                // begin execution of the next in line
                if (queue.size >= threshold) {
                    // Grab a reference to the next callback in the queue
                    const nextCallback = Array.from(queue)[threshold - 1];

                    // Trigger it!
                    nextCallback().catch((err) => { /* ignore errors, they're actually caught above! */ });
                }
            }
        };

        // Push our wrapped callback to the execution queue
        queue.add(wrappedCallback);

        // If the queue isn't full yet, then dispatch the callback right
        // away
        if (queue.size <= threshold) {
            wrappedCallback().catch((err) => { /* ignore errors, they're actually caught above! */ });
        }

        // Return the promise externally, so we can reliably wait for the
        // execution to complete...
        return promise;
    };
}
